/**
 * The audio library's two routes and the words each is filed under (Dax, 2026-09-23). Music carries a
 * genre and a mood, the two things a person asks for ("something upbeat and pop"); a sound effect
 * carries a category and the setting it belongs in. The lists are fixed, so the listening pass chooses
 * from them, the Browse filters are dropdowns, and the composer's words meet the same vocabulary.
 */
/** Music genres, broad enough that a short-form creator recognises every one. */
export const MUSIC_GENRES = [
    'pop', 'hip hop', 'r&b', 'soul', 'funk', 'electronic', 'dance', 'house', 'techno', 'amapiano', 'afrobeats',
    'latin', 'reggae', 'rock', 'indie', 'alternative', 'acoustic', 'folk', 'country', 'jazz', 'classical',
    'cinematic', 'ambient', 'lo-fi', 'world', 'children',
];
/** What the music feels like: the emotion or sentiment it carries under a cut. */
export const MUSIC_MOODS = [
    'happy', 'uplifting', 'energetic', 'playful', 'confident', 'inspiring', 'romantic', 'sexy', 'calm',
    'relaxed', 'dreamy', 'nostalgic', 'emotional', 'sad', 'dark', 'tense', 'dramatic', 'epic', 'mysterious', 'quirky',
];
/** Standard sound-effect categories, the way effect libraries file them. */
export const SFX_CATEGORIES = [
    'foley', 'impacts', 'whooshes and transitions', 'interface and notifications', 'food and drink', 'kitchen',
    'household', 'nature', 'weather', 'water', 'animals', 'people and voices', 'crowds', 'vehicles', 'machines',
    'technology', 'cartoon and comedy', 'horror and creatures', 'sci-fi', 'weapons and explosions',
    'musical stingers', 'ambience',
];
/** Where a sound effect belongs: the environment it sounds like it was recorded in. */
export const SFX_SETTINGS = [
    'indoor', 'outdoor', 'kitchen', 'home', 'office', 'studio', 'street', 'city', 'countryside', 'forest',
    'beach', 'underwater', 'vehicle', 'crowd', 'space', 'fantasy',
];
export function audioRoute(kind) {
    return kind === 'sfx' ? 'sfx' : kind === 'voice' ? 'voice' : 'music';
}
/** The kinds on each route, for queries that must never hand a music channel a door slam. */
export const ROUTE_KINDS = { music: ['bed', 'track'], sfx: ['sfx'], voice: ['voice'] };
/** Publishers of effect libraries a fingerprint can match; their "artist" is a catalogue, not a musician. */
const SFX_PUBLISHERS = /^(sound ideas|hollywood edge|the hollywood edge|bbc sound effects|pro sound effects|boom library|soundsnap|zapsplat|sonniss|blastwave fx|soundmorph|sound dogs|sounddogs)$/i;
/** An album or label that names itself as effects: "Aliens Sound Effects". */
const SFX_WORDS = /\b(sound ?effects?|sfx|foley)\b/i;
/**
 * Whether a fingerprint match is an effects library rather than a recording. Measured 2026-09-23:
 * ACRCloud matched an alien vocal as "Electro Vocal from Alien Creature" by "Sound Ideas", album "Aliens
 * Sound Effects", genres "Films/Games"; a Tate McRae track came back with a record label and "Pop".
 */
export function soundLibraryMatch(match) {
    const artists = (match.artist ?? '').split(',').map((a) => a.trim()).filter(Boolean);
    return artists.some((a) => SFX_PUBLISHERS.test(a)) || SFX_WORDS.test(match.album ?? '') || SFX_WORDS.test(match.label ?? '');
}
/** ACRCloud's genre names that are ours by another spelling. Anything unlisted is matched by name or dropped. */
const ACR_GENRE_ALIASES = {
    'hip-hop': ['hip hop'], 'hip hop/rap': ['hip hop'], 'hip-hop/rap': ['hip hop'], rap: ['hip hop'],
    'r&b/soul': ['r&b', 'soul'], rnb: ['r&b'], 'r&b': ['r&b'], 'electronica': ['electronic'], edm: ['electronic', 'dance'],
    'dance/electronic': ['dance', 'electronic'], 'singer/songwriter': ['acoustic'], soundtrack: ['cinematic'],
    'film scores': ['cinematic'], 'films/games': ['cinematic'], 'children\'s music': ['children'], 'lo fi': ['lo-fi'], lofi: ['lo-fi'],
    'latin pop': ['latin', 'pop'], 'k-pop': ['pop'], 'j-pop': ['pop'], 'afrobeat': ['afrobeats'], 'afro house': ['house', 'afrobeats'],
};
/** A recording's genres as ACRCloud names them, read into ours; at most three, in the order given. */
export function genresFromAcr(names) {
    const known = new Set(MUSIC_GENRES);
    const out = [];
    for (const raw of names) {
        const name = raw.trim().toLowerCase();
        const mapped = ACR_GENRE_ALIASES[name] ?? (known.has(name) ? [name] : []);
        for (const g of mapped)
            if (!out.includes(g))
                out.push(g);
    }
    return out.slice(0, 3);
}
//# sourceMappingURL=audio-taxonomy.js.map